import { MessageCircleQuestion, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The manual's side of the seam to the "Ask Fleetwise" help bubble.
 *
 * The bubble is mounted app-wide and listens for this event; the manual only
 * announces the intent. Keeping it a DOM event rather than shared context means
 * neither surface imports the other — /help stays a pure reading page, and the
 * bubble stays independently ownable.
 */
export const OPEN_HELP_EVENT = 'fleetwise:open-help';

export function openHelpAgent(): void {
  window.dispatchEvent(new CustomEvent(OPEN_HELP_EVENT));
}

/** Compact header affordance — always in reach while reading. */
export function AskFleetwiseButton({ className }: { className?: string }) {
  return (
    <button
      type="button"
      onClick={openHelpAgent}
      title="Ask Fleetwise a question"
      className={cn(
        'border-fw-border bg-fw-surface text-fw-text-secondary hover:bg-fw-surface-hover hover:text-fw-text hover:border-fw-border-strong active:bg-fw-surface focus-visible:ring-fw-ring inline-flex h-11 items-center gap-1.5 rounded-full border px-3 text-sm font-medium transition-colors duration-150 focus-visible:ring-2',
        className,
      )}
    >
      <Sparkles size={16} strokeWidth={1.75} className="text-fw-accent" aria-hidden />
      <span className="hidden sm:inline">Ask Fleetwise</span>
      <span className="sr-only sm:hidden">Ask Fleetwise</span>
    </button>
  );
}

/**
 * The prominent invitation. Reading a manual is the fallback — asking in your
 * own words is the product — so this is deliberately louder than a link and
 * sits above the chapter grid rather than under it.
 */
export function AskFleetwiseCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'border-fw-accent/35 bg-fw-accent-soft rounded-2xl border p-5 sm:p-6',
        className,
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <div className="flex min-w-0 items-start gap-3.5">
          <span
            className="bg-fw-bg/60 text-fw-accent border-fw-accent/30 flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border"
            aria-hidden
          >
            <MessageCircleQuestion size={22} strokeWidth={1.75} />
          </span>
          <div className="min-w-0">
            <p className="text-fw-text text-base font-semibold sm:text-lg">
              Faster than hunting: just ask.
            </p>
            <p className="text-fw-text-secondary mt-1 max-w-xl text-sm leading-relaxed">
              Describe what you're trying to do in your own words. Fleetwise answers out of this
              same manual and points you at the exact section.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openHelpAgent}
          className="fw-button-primary focus-visible:ring-fw-ring h-11 shrink-0 px-5 text-sm font-semibold focus-visible:ring-2"
        >
          <Sparkles size={16} strokeWidth={2} aria-hidden />
          Ask Fleetwise
        </button>
      </div>
    </div>
  );
}
